import React from "react";
import {AuditOutlined, ClockCircleOutlined} from "@ant-design/icons";
import {Tooltip} from "antd";
import {TranslateFunction} from "react-localize-redux";
import {useSelector} from "react-redux";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {selectTranslate} from "../../store/selectors/translate";
import {JobTag, TestScoreStatus} from "../../enums/enums";
import {formatString} from "../../utils/common";
import "./TestScore.scss";

interface Props {
    data: JobEntity
}

const TestScore: React.FC<Props> = ({data: {scoreStatus, testScoreStatistics, decoratedStatus}}: Props): React.ReactElement => {
    const translatePath: string = "components.TestScore.TestScore";
    const translate: TranslateFunction = useSelector(selectTranslate);
    const {PASS, FAILED, NOT_EVALUATED}: typeof TestScoreStatus = TestScoreStatus;

    /**
     * When decorated status is expired we return expired test template
     * @return {React.ReactNode}
     */
    const getExpiredStatus = (): React.ReactNode => (
        <Tooltip title={translate(`${translatePath}.expiredTooltip.text`)}>
            <div className="df-test-score df-test-expired">
                <ClockCircleOutlined/>
            </div>
        </Tooltip>
    );

    /**
     * Based on test results data we print test score result in appropriate colors
     * @return {React.ReactNode}
     */
    const getTestScore = (): React.ReactNode => {
        let tooltipLocale: string = "inEvaluation";
        let template: React.ReactNode = (
            <div className="df-test-score">
                <AuditOutlined/>
            </div>
        );

        if (testScoreStatistics && testScoreStatistics.scoreValue >= 0) {
            const testScore: string = testScoreStatistics.scoreValue.toString();
            let cls: string = "";
            switch (scoreStatus) {
                case PASS:
                    cls = "df-test-pass-score";
                    tooltipLocale = "passTooltip";
                    break;
                case FAILED:
                    cls = "df-test-fail-score";
                    tooltipLocale = "failTooltip";
                    break;
                case NOT_EVALUATED:
                    tooltipLocale = "scoreTooltip";
                    break;
                default:
                    break;
            }

            template = (
                <div className={`df-test-score ${cls}`}>
                    {formatString(translate(`${translatePath}.percentageFormat.text`) as string, testScore)}
                </div>
            );
        }

        return (
            <Tooltip title={translate(`${translatePath}.${tooltipLocale}.text`)}>
                {template}
            </Tooltip>
        );
    };

    return (
        <>
            {decoratedStatus === JobTag.EXPIRED ? getExpiredStatus() : getTestScore()}
        </>
    );
};

export default TestScore;
